<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import CallsignTraining from './components/CallsignTraining.vue'
import PropagationPredictor from './components/PropagationPredictor.vue'
import AntennaCalculator from './components/AntennaCalculator.vue'
import FrequencyCalculator from './components/FrequencyCalculator.vue'
import MorseCodeConverter from './components/MorseCodeConverter.vue'
import SSTVEncoder from './components/SSTVEncoder.vue'

// 简单路由
const currentPath = ref(window.location.hash || '#/')

// 移动视图状态
const isMobileView = ref(false)

// 检查是否为移动视图
function checkMobileView() {
  isMobileView.value = window.innerWidth < 768
}

// 监听 hash 变化
window.addEventListener('hashchange', () => {
  currentPath.value = window.location.hash
})

// 根据路径显示不同组件
const currentView = computed(() => {
  switch (currentPath.value) {
    case '#/callsign':
      return CallsignTraining
    case '#/propagation':
      return PropagationPredictor
    case '#/antenna':
      return AntennaCalculator
    case '#/frequency':
      return FrequencyCalculator
    case '#/morse':
      return MorseCodeConverter
    case '#/sstv':
      return SSTVEncoder
    default:
      return CallsignTraining
  }
})

// 组件挂载时初始化
onMounted(() => {
  // 如果URL中没有hash，默认添加#/callsign
  if (!window.location.hash) {
    window.location.hash = '#/callsign'
  }

  // 检查是否为移动视图
  checkMobileView()
  window.addEventListener('resize', checkMobileView)
})

// 组件卸载前移除事件监听
onBeforeUnmount(() => {
  window.removeEventListener('resize', checkMobileView)
})
</script>

<template>
  <main>
    <!-- 内容区域 -->
    <div class="content-area">
      <component :is="currentView" />
    </div>

    <!-- 底部导航 - 移动端 -->
    <div class="bottom-nav" v-if="isMobileView">
      <a href="#/callsign" :class="{ active: currentPath === '#/callsign' || currentPath === '#/' }">
        <span class="nav-icon">📻</span>
        <span class="nav-text">呼号</span>
      </a>
      <a href="#/propagation" :class="{ active: currentPath === '#/propagation' }">
        <span class="nav-icon">📡</span>
        <span class="nav-text">传播</span>
      </a>
      <a href="#/antenna" :class="{ active: currentPath === '#/antenna' }">
        <span class="nav-icon">📶</span>
        <span class="nav-text">天线</span>
      </a>
      <a href="#/frequency" :class="{ active: currentPath === '#/frequency' }">
        <span class="nav-icon">🔢</span>
        <span class="nav-text">频率</span>
      </a>
      <a href="#/morse" :class="{ active: currentPath === '#/morse' }">
        <span class="nav-icon">⚡</span>
        <span class="nav-text">电码</span>
      </a>
      <a href="#/sstv" :class="{ active: currentPath === '#/sstv' }">
        <span class="nav-icon">📺</span>
        <span class="nav-text">图像编码</span>
      </a>
    </div>

    <!-- 顶部导航 - 桌面端 -->
    <div class="navigation-tabs" v-if="!isMobileView">
      <a href="#/callsign" :class="{ active: currentPath === '#/callsign' || currentPath === '#/' }">呼号训练</a>
      <a href="#/propagation" :class="{ active: currentPath === '#/propagation' }">传播预测</a>
      <a href="#/antenna" :class="{ active: currentPath === '#/antenna' }">天线计算</a>
      <a href="#/frequency" :class="{ active: currentPath === '#/frequency' }">频率计算</a>
      <a href="#/morse" :class="{ active: currentPath === '#/morse' }">莫尔斯电码</a>
      <a href="#/sstv" :class="{ active: currentPath === '#/sstv' }">SSTV图像编码</a>
    </div>
  </main>
</template>

<style>
body {
  margin: 0;
  padding: 0;
  font-family: Arial, sans-serif;
  background-color: #f5f5f5;
  color: #333;
}

main {
  max-width: 800px;
  margin: 20px auto;
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  position: relative;
}

.navigation-tabs {
  display: flex;
  background-color: #f0f0f0;
  border-bottom: 1px solid #ddd;
  overflow-x: auto;
  white-space: nowrap;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none; /* Firefox */
}

.navigation-tabs::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Edge */
}

.navigation-tabs a {
  padding: 12px 16px;
  color: #555;
  text-decoration: none;
  font-size: 15px;
  transition: all 0.2s ease;
  border-bottom: 3px solid transparent;
}

.navigation-tabs a:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

.navigation-tabs a.active {
  color: #3a8c3a;
  border-bottom: 3px solid #3a8c3a;
  font-weight: 500;
}

.content-area {
  padding: 20px;
  padding-bottom: 80px; /* 为移动端底部导航留出空间 */
}

/* 底部导航 - 移动端 */
.bottom-nav {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-around;
  background-color: white;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  padding: 5px 0;
}

.bottom-nav a {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #666;
  text-decoration: none;
  flex: 1;
  transition: all 0.2s ease;
  padding: 5px 0;
}

.bottom-nav a.active {
  color: #3a8c3a;
}

.nav-icon {
  font-size: 20px;
  margin-bottom: 2px;
}

.nav-text {
  font-size: 12px;
  margin-top: 2px;
}

/* 响应式调整 */
@media (max-width: 830px) {
  main {
    margin: 10px 15px 50px 15px; /* 减小底部边距，因为导航栏现在更小 */
    width: auto;
    border-radius: 10px 10px 0 0;
  }

  .content-area {
    padding-bottom: 60px; /* 减小内容区域底部内边距 */
  }

  .navigation-tabs a {
    padding: 10px 14px;
    font-size: 14px;
  }
}

/* 移动端适配 */
@media (max-width: 768px) {
  main {
    margin: 0 0 50px 0; /* 移动端全宽显示 */
    border-radius: 0;
  }

  .content-area {
    padding: 15px 10px 60px 10px; /* 减小内边距，提供更多内容空间 */
  }
}

/* 深色模式切换按钮样式 */
.theme-toggle {
  position: fixed;
  bottom: 20px;
  right: 20px;
  z-index: 1001;
}

.theme-toggle-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: white;
  border: 1px solid #ddd;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
}

/* 系统深色模式适配 */
@media (prefers-color-scheme: dark) {
  body {
    background-color: #121212;
    color: #e0e0e0;
  }

  main {
    background-color: #1e1e1e;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  }

  .navigation-tabs {
    background-color: #252525;
    border-bottom-color: #333;
  }

  .navigation-tabs a {
    color: #bbb;
  }

  .navigation-tabs a:hover {
    background-color: rgba(255, 255, 255, 0.05);
  }

  .navigation-tabs a.active {
    color: #7cb342;
    border-bottom-color: #7cb342;
  }

  .bottom-nav {
    background-color: #1e1e1e;
    box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
  }

  .bottom-nav a {
    color: #aaa;
  }

  .bottom-nav a.active {
    color: #7cb342;
  }

  .theme-toggle-btn {
    background-color: #333;
    border-color: #555;
    color: #e0e0e0;
  }
}

/* 手动深色模式样式 */
html.dark-mode body {
  background-color: #121212;
  color: #e0e0e0;
}

html.dark-mode main {
  background-color: #1e1e1e;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
}

html.dark-mode .navigation-tabs {
  background-color: #252525;
  border-bottom-color: #333;
}

html.dark-mode .navigation-tabs a {
  color: #bbb;
}

html.dark-mode .navigation-tabs a:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

html.dark-mode .navigation-tabs a.active {
  color: #7cb342;
  border-bottom-color: #7cb342;
}

html.dark-mode .bottom-nav {
  background-color: #1e1e1e;
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
}

html.dark-mode .bottom-nav a {
  color: #aaa;
}

html.dark-mode .bottom-nav a.active {
  color: #7cb342;
}

html.dark-mode .theme-toggle-btn {
  background-color: #333;
  border-color: #555;
  color: #e0e0e0;
}
</style>

